import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { INVOICE_NUMBER_MAX_LENGTH, SupportRequestFormData } from '../schemas/supportRequestFormSchema';
import { SearchableSelect } from '@/pages/newSupported/components/LabeledInput';
import RadioGroup, { RadioOption } from '@/pages/newSupported/components/RadioGroup';
import InfoPanel, { InfoPanelItem } from '@/components/ui/InfoPanel/InfoPanel';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import useFundContext from '@/contexts/Fund/useFundContext';
import useBanks from '@/hooks/useBanks';
import warningIcon from '@/assets/icons/info2.svg';
import { useEffect, useMemo, useState } from 'react';
import { buildAvailableBalancePendingTooltipText } from '@/helpers/availableBalanceTooltipText';
import SupportAmountInput from '../SupportAmountInput';
import UpcomingStandingSupportsNotice from '../UpcomingStandingSupportsNotice';
import wizardStyles from '@/pages/SupportRequests/NewSupportRequest.module.scss';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { FUND_EXPENSE_GOAL_NAMES } from '@/constants/fundExpenseGoals';
import { SupplierFullDtoIn } from '@/models/in/SupplierFullDtoIn';
import { ProjectRefDtoIn } from '@/models/in/ProjectRefDtoIn';

const formatCurrency = (value: number) => new Intl.NumberFormat('he-IL').format(value) + ' ₪';

interface FundPaymentSetupProps {
  /**
   * HAT-1105: set when the project was chosen BEFORE the form, in the request dialog's "בחירת מיזם"
   * step. It is then shown, not asked again - a second picker under the first would invite choosing a
   * different project from the one the supplier was picked for.
   */
  lockedProjectName?: string | null;
}

const FundPaymentSetup = ({ lockedProjectName }: FundPaymentSetupProps) => {
  const { t } = useTranslation();
  const { currentFund, balance } = useFundContext();
  const { watch, setValue, control } = useFormContext<SupportRequestFormData>();

  const amount = watch('amount');
  const isAlreadyPaid = watch('isAlreadyPaid');
  const bankCode = watch('bankCode');
  const goalWithLimits = watch('goalWithLimits');
  const selectedSupplierId = watch('selectedSupplierId');
  const supplierPaymentInfoId = watch('supplierPaymentInfoId');
  const exceptionsCommitteeAcknowledged = watch('exceptionsCommitteeAcknowledged');
  const exceedsBalanceComment = watch('exceedsBalanceComment');
  const invoiceNumber = watch('invoiceNumber');
  const supplierTaxId = watch('supplierTaxId');
  const isDuplicateInvoiceNumber = watch('isDuplicateInvoiceNumber');
  const projectId = watch('projectId');

  // Approved projects of this fund, for the optional "פרויקט" picker below.
  const { data: selectableProjectsData } = useGetAuthApi<ProjectRefDtoIn[]>(currentFund?.id ? `funds/${currentFund.id}/projects/selectable` : '');
  const selectableProjects = selectableProjectsData ?? [];

  const isFundraisingGoal = goalWithLimits?.goal?.name === FUND_EXPENSE_GOAL_NAMES.FUNDRAISING;

  const { data: fundraisingEligibleAmount } = useGetAuthApi<number>(
    isFundraisingGoal && currentFund?.id ? `funds/${currentFund.id}/fundraising-eligible-amount` : ''
  );

  // Load suppliers to get paymentInfos for the selected supplier (bank account picker)
  const { data: previousSuppliers } = useGetAuthApi<SupplierFullDtoIn[]>(
    currentFund?.id && typeof selectedSupplierId === 'number' && selectedSupplierId > 0 ? `funds/${currentFund.id}/suppliers` : ''
  );

  const selectedSupplier = useMemo(
    () => (typeof selectedSupplierId === 'number' && selectedSupplierId > 0 ? previousSuppliers?.find((s) => s.id === selectedSupplierId) : undefined),
    [selectedSupplierId, previousSuppliers]
  );

  const supplierPaymentInfos = useMemo(() => selectedSupplier?.paymentInfos ?? [], [selectedSupplier]);
  const showStoredBankAccountPicker = !isAlreadyPaid && supplierPaymentInfos.length > 0;

  // Real-time duplicate check: as the user types an invoice number, ask the server whether
  // this supplier+invoice combination was already used, instead of waiting for final submit.
  // Debounced so we don't hit the API on every keystroke.
  const [debouncedInvoiceNumber, setDebouncedInvoiceNumber] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedInvoiceNumber((invoiceNumber ?? '').trim()), 400);
    return () => clearTimeout(id);
  }, [invoiceNumber]);

  const hasSupplierIdentity = (typeof selectedSupplierId === 'number' && selectedSupplierId > 0) || !!supplierTaxId?.trim();

  const checkInvoiceNumberUrl = useMemo(() => {
    if (!currentFund?.id || !debouncedInvoiceNumber || !hasSupplierIdentity) return '';
    const params = new URLSearchParams({ invoiceNumber: debouncedInvoiceNumber });
    if (typeof selectedSupplierId === 'number' && selectedSupplierId > 0) {
      params.set('supplierId', String(selectedSupplierId));
    } else if (supplierTaxId?.trim()) {
      params.set('supplierTaxId', supplierTaxId.trim());
    }
    return `funds/${currentFund.id}/support-requests/check-invoice-number?${params.toString()}`;
  }, [currentFund?.id, debouncedInvoiceNumber, hasSupplierIdentity, selectedSupplierId, supplierTaxId]);

  const { data: isInvoiceNumberDuplicate } = useGetAuthApi<boolean>(checkInvoiceNumberUrl);

  useEffect(() => {
    setValue('isDuplicateInvoiceNumber', checkInvoiceNumberUrl ? !!isInvoiceNumberDuplicate : false);
  }, [checkInvoiceNumberUrl, isInvoiceNumberDuplicate, setValue]);

  useEffect(() => {
    if (isAlreadyPaid) return;
    if (!supplierPaymentInfoId || supplierPaymentInfoId === 0) {
      setValue('bankCode', null);
      setValue('bankBranch', '');
      setValue('bankAccount', '');
      return;
    }
    const pickedAccount = supplierPaymentInfos.find((pi) => pi.id === supplierPaymentInfoId);
    if (!pickedAccount) return;
    setValue('bankCode', pickedAccount.bankCode);
    setValue('bankBranch', pickedAccount.bankBranch);
    setValue('bankAccount', pickedAccount.bankAccount);
  }, [supplierPaymentInfoId, supplierPaymentInfos, setValue, isAlreadyPaid]);

  useEffect(() => {
    if (isAlreadyPaid) {
      setValue('supplierPaymentInfoId', 0);
      setValue('bankCode', null);
      setValue('bankBranch', '');
      setValue('bankAccount', '');
    } else {
      setValue('payerName', null);
      setValue('payerIsraeliId', null);
      setValue('bankCode', null);
      setValue('bankBranch', '');
      setValue('bankAccount', '');
    }
  }, [isAlreadyPaid, setValue]);

  const lockSupplierBankFields = !isAlreadyPaid && !!supplierPaymentInfoId && supplierPaymentInfoId > 0;

  const availableBalance = balance?.calculateAvailableBalance() ?? 0;
  // HAT-1396: the payment costs the fund its support-row fee on top of the amount (server checks the same).
  const supportRowFee = balance?.supportRequestsBalances?.supportRowFee ?? 0;
  const exceedsAvailableBalance = amount > 0 && amount + supportRowFee > availableBalance;
  const exceedsFundraisingLimit = isFundraisingGoal && amount > 0 && fundraisingEligibleAmount !== undefined && amount > fundraisingEligibleAmount;

  useEffect(() => {
    setValue('exceedsAvailableBalance', exceedsAvailableBalance);
    setValue('exceedsEligibleAmount', exceedsFundraisingLimit);
    if (!exceedsAvailableBalance && !exceedsFundraisingLimit) {
      setValue('exceptionsCommitteeAcknowledged', false);
      setValue('exceedsBalanceComment', '');
    }
  }, [exceedsAvailableBalance, exceedsFundraisingLimit, setValue]);

  const paymentStatusOptions: RadioOption[] = [
    { value: 'false', label: t('notYetPaid') },
    { value: 'true', label: t('alreadyPaid') },
  ];

  const getFundraisingEligibleDisplay = () => {
    if (!isFundraisingGoal) return t('noLimit');
    if (fundraisingEligibleAmount === undefined) return t('calculating');
    return formatCurrency(Math.max(0, fundraisingEligibleAmount));
  };

  const fundBalanceItems: InfoPanelItem[] = [
    {
      label: t('fundBalance'),
      value: balance ? formatCurrency(balance.balancesByKind?.total ?? 0) : '—',
    },
    {
      label: t('availableBalance'),
      value: balance ? formatCurrency(balance.calculateAvailableBalance()) : '—',
      hasInfoIcon: true,
      infoTooltip: balance
        ? buildAvailableBalancePendingTooltipText(t, formatCurrency, balance.supportRequestsBalances)
        : '',
    },
    {
      label: t('amountLimitByClassification'),
      value: getFundraisingEligibleDisplay(),
    },
  ];

  return (
    <div className={`w-full max-w-[1132px] px-4 flex flex-row gap-6 ${wizardStyles.paymentSetupRow}`}>
      {/* Main Form Section */}
      <div className="flex-1">
        {/* Section 1: Support Amount */}
        <div className="flex flex-col gap-6">
          <h2 className="body-lr-bold text-(--font-sub-title-2)">{t('supportAmount')}</h2>

          <div className="w-full max-w-[402px]">
            <SupportAmountInput
              id="fund-support-request-amount"
              label={t('supportAmount')}
              amount={amount}
              onAmountChange={(next) => setValue('amount', next)}
              placeholder={t('enterAmount')}
              className="border-(--border-input-default) rounded-md"
            />
            {supportRowFee > 0 && (
              <p className="body-sm-regular text-(--font-body-2) mt-1">
                {t('supportOverheadHint', { fee: formatCurrency(supportRowFee) })}
              </p>
            )}
          </div>

          <div className="w-full max-w-[402px]">
            <label className="body-sm-regular text-(--font-heading) block mb-1.5">{t('invoiceNumber')}</label>
            <input
              type="text"
              maxLength={INVOICE_NUMBER_MAX_LENGTH}
              className={`body-md-regular text-(--font-body-1) p-3 border rounded-md w-full ${
                isDuplicateInvoiceNumber ? 'border-(--border-primary)' : 'border-(--border-input-default)'
              }`}
              value={invoiceNumber || ''}
              onChange={(e) => setValue('invoiceNumber', e.target.value)}
              placeholder={t('invoiceNumberPlaceholder') || ''}
            />
            {isDuplicateInvoiceNumber && <p className="body-sm-regular text-(--font-primary) mt-1">{t('duplicateInvoiceNumber')}</p>}
          </div>

          {/* Booking this expense against a project is optional, and only APPROVED projects of
              this fund are offered - the endpoint enforces that server-side too, so a
              coordinator cannot spend against a project the office has not approved. Hidden
              entirely when the fund has no approved project, rather than shown empty. */}
          {lockedProjectName ? (
            <div className="w-full max-w-[402px]">
              <span className="body-sm-regular text-(--font-heading) block mb-1.5">{t('projects.supportRequest.projectLabel')}</span>
              <p className="body-md-regular text-(--font-body-1) p-3 border border-(--border-input-default) rounded-md w-full bg-(--surface-grey-low-2)">{lockedProjectName}</p>
            </div>
          ) : selectableProjects.length > 0 && (
            <div className="w-full max-w-[402px]">
              <label htmlFor="support-request-project" className="body-sm-regular text-(--font-heading) block mb-1.5">
                {t('projects.supportRequest.projectLabel')}
              </label>
              <select
                id="support-request-project"
                className="body-md-regular text-(--font-body-1) p-3 border border-(--border-input-default) rounded-md w-full bg-white"
                value={projectId ?? ''}
                onChange={(e) => setValue('projectId', e.target.value === '' ? null : Number(e.target.value))}
              >
                <option value="">{t('projects.supportRequest.projectPlaceholder')}</option>
                {selectableProjects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex gap-4 w-full max-w-[402px]">
            <RadioGroup
              options={paymentStatusOptions}
              value={isAlreadyPaid ? 'true' : 'false'}
              onChange={(value) => setValue('isAlreadyPaid', value === 'true')}
              name="isAlreadyPaid"
              direction="horizontal"
            />
          </div>
        </div>

        {/* Section 3: Payment Details */}
        <>
          <div className="my-8 border-t border-(--border-lowest)" />

          <div className="flex flex-col gap-4">
            <h3 className="body-lr-bold text-(--font-sub-title-2)">{isAlreadyPaid ? t('payerDetails') : t('supplierBankDetails')}</h3>

            {showStoredBankAccountPicker && (
              <div className="w-full max-w-[600px]">
                <label className="body-sm-regular text-(--font-heading) block mb-1.5">{t('selectExistingBankAccountOrNew')}</label>
                <select
                  className="body-md-regular text-(--font-body-1) p-3 border border-(--border-input-default) rounded-md w-full"
                  value={supplierPaymentInfoId ?? 0}
                  onChange={(e) => setValue('supplierPaymentInfoId', Number(e.target.value))}
                >
                  <option value={0}>{t('newBankAccount')}</option>
                  {supplierPaymentInfos.map((pi) => (
                    <option key={pi.id} value={pi.id}>
                      {`${t('bank')} ${pi.bankCode}, ${t('branch')} ${pi.bankBranch}, ${t('account')} ${pi.bankAccount}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {isAlreadyPaid && (
              <div className="flex gap-8">
                <div className="flex-1">
                  <label className="body-sm-regular text-(--font-heading) block mb-1.5">{t('payerName')}</label>
                  <input
                    type="text"
                    className="body-md-regular text-(--font-body-1) p-3 border border-(--border-input-default) rounded-md w-full"
                    value={watch('payerName') || ''}
                    onChange={(e) => setValue('payerName', e.target.value)}
                    placeholder={t('pleaseEnterName') || ''}
                  />
                </div>
                <div className="flex-1">
                  <label className="body-sm-regular text-(--font-heading) block mb-1.5">{t('payerIsraeliId')}</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="body-md-regular text-(--font-body-1) p-3 border border-(--border-input-default) rounded-md w-full"
                    value={watch('payerIsraeliId') || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*$/.test(value) && value.length <= 9) setValue('payerIsraeliId', value);
                    }}
                    placeholder={t('pleaseEnterId') || ''}
                  />
                </div>
              </div>
            )}

            <div className="flex gap-8">
              <div className="flex-1">
                <Controller
                  name="bankCode"
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      label={t('bankCode')}
                      width="100%"
                      value={field.value != null ? String(field.value) : ''}
                      onChange={(value) => {
                        const bankNum = parseInt(value.split(' - ')[0]);
                        field.onChange(isNaN(bankNum) ? null : bankNum);
                      }}
                      loadOptions={useBanks.getBankOptions}
                      placeholder="bankCodePlaceholder"
                      required
                      disabled={lockSupplierBankFields}
                    />
                  )}
                />
              </div>
              <div className="flex-1">
                <Controller
                  name="bankBranch"
                  control={control}
                  render={({ field }) => {
                    const bankValue = bankCode ? `${bankCode} - ` : '';
                    return (
                      <SearchableSelect
                        label={t('bankBranch')}
                        width="100%"
                        value={field.value ?? ''}
                        onChange={(value) => {
                          const branchNum = value.split(' - ')[0];
                          field.onChange(branchNum);
                        }}
                        loadOptions={useBanks.getBranchOptions}
                        dependency={bankValue}
                        placeholder="branchNumberPlaceholder"
                        dependencyPlaceholder="selectBankFirst"
                        required
                        disabled={lockSupplierBankFields}
                      />
                    );
                  }}
                />
              </div>
              <div className="flex-1">
                <label className={`body-sm-regular block mb-1.5 ${lockSupplierBankFields ? 'text-(--font-disabled)' : 'text-(--font-heading)'}`}>
                  {t('accountNumber')}
                </label>
                <input
                  type="text"
                  className={`body-md-regular p-3 border border-(--border-input-default) rounded-md w-full ${lockSupplierBankFields ? 'text-(--font-disabled) bg-(--surface-grey-low-2) cursor-not-allowed' : 'text-(--font-body-1)'}`}
                  value={watch('bankAccount') || ''}
                  onChange={(e) => setValue('bankAccount', e.target.value.replace(/\D/g, ''))}
                  placeholder={t('accountNumberPlaceholder') || ''}
                  disabled={lockSupplierBankFields}
                />
              </div>
            </div>
          </div>
        </>

        {/* SPR-10304: standing supports about to fall due are no longer taken off the available
            balance; the coordinator is told about them here instead, while deciding on the amount. */}
        <UpcomingStandingSupportsNotice upcoming={balance?.supportRequestsBalances?.upcomingStandingSupports} className="mt-8" />

        {/* Exceeds Balance / Fundraising Limit Warning */}
        {(exceedsAvailableBalance || exceedsFundraisingLimit) && (
          <>
            <div className="my-8 border-t border-(--border-lowest)" />

            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-start gap-3 px-4">
                <img
                  src={warningIcon}
                  alt="warning"
                  className="w-4 h-4"
                  style={{ filter: 'brightness(0) saturate(100%) invert(15%) sepia(60%) saturate(2000%) hue-rotate(320deg)' }}
                />
                <p className="body-lr-regular text-(--font-body-1)">
                  {exceedsAvailableBalance && exceedsFundraisingLimit && t('exceedsWarningBalanceAndFundraisingLimit')}
                  {exceedsAvailableBalance && !exceedsFundraisingLimit && t('exceedsWarningBalanceOnly')}
                  {!exceedsAvailableBalance && exceedsFundraisingLimit && t('exceedsWarningFundraisingLimitOnly')}
                </p>
              </div>
              <div className="flex items-center justify-start gap-4 px-5 py-2 rounded-full w-full" style={{ backgroundColor: 'var(--surface-grey-low-2)' }}>
                <Checkbox checked={exceptionsCommitteeAcknowledged} onCheckedChange={(checked) => setValue('exceptionsCommitteeAcknowledged', checked)} />
                <p className="body-lr-regular text-(--font-body-1)">{t('exceptionsCommitteeAcknowledgement')}</p>
              </div>

              <div className="px-4">
                <label className="body-sm-regular text-(--font-heading) block mb-1.5">
                  {t('exceedsBalanceCommentLabel')}
                  <span className="text-red-500"> *</span>
                </label>
                <textarea
                  className="body-md-regular text-(--font-body-1) p-3 border border-(--border-main) rounded-lg w-full resize-none"
                  rows={3}
                  placeholder={t('exceedsBalanceCommentPlaceholder')}
                  value={exceedsBalanceComment || ''}
                  onChange={(e) => setValue('exceedsBalanceComment', e.target.value)}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Fund Balance Info Panel */}
      <InfoPanel title={t('fundAndSupportedBalanceData')} items={fundBalanceItems} className="w-[240px] max-md:w-full shrink-0" />
    </div>
  );
};

export default FundPaymentSetup;
